/**
 * The one slot on the product's patient page. Renders nothing unless the demo
 * layer is on for this organization and the patient is bound to a demo patient.
 *
 * Top to bottom (2.5): what needs attention, how far along the plan of care is,
 * one calendar carrying every discipline — with the episode plan's own detail a
 * click away under it — and what the patient told us.
 */
import { Accordion, AccordionDetails, AccordionSummary, Card, CardContent, Skeleton, Stack } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useOrganization } from "~/providers/GraphqlProvider";
import { isHomeHealthDemoEnabled } from "../../demoEnabled";
import type { DemoState } from "@caredna-marlin/utils/homeHealthDemo/state";
import { useDemoSelector } from "../../store/useDemoStore";
import { EpisodePlanSection } from "./EpisodePlanSection";
import { PatientAvailabilityReport } from "./PatientAvailabilityReport";
import { PlanProgressSection } from "./PlanProgressSection";
import { PatientAlertsPanel, usePatientAlertRows } from "../worklist/PatientAlertsPanel";

/**
 * The fixture patient this product page is showing, if any. The id is the fast
 * path and the name is the reliable one; see the note on the section below.
 * Exported because the calendar button beside the product's own headings has to
 * answer the same question without rendering the section.
 */
export function patientRefForPage(
  s: Pick<DemoState, "meta" | "patients">,
  patientId: number | undefined,
  patientName: { firstName?: string | null; lastName?: string | null } | undefined,
): string | undefined {
  const byId = patientId === undefined ? undefined : Object.entries(s.meta.bindings.patientIdsByRef).find(([, id]) => id === patientId)?.[0];
  if (byId) return byId;
  const first = patientName?.firstName?.trim().toLowerCase();
  const last = patientName?.lastName?.trim().toLowerCase();
  if (!first || !last) return undefined;
  return Object.values(s.patients).find((p) => p.firstName.trim().toLowerCase() === first && p.lastName.trim().toLowerCase() === last)?.ref;
}

export function HomeHealthPatientSection({ patientId, patientName }: { patientId?: number; patientName?: { firstName?: string | null; lastName?: string | null } }) {
  const { organizationName } = useOrganization();
  const enabled = isHomeHealthDemoEnabled(organizationName);
  /**
   * **The id is the fast path, the name is the reliable one.** `patientIdsByRef`
   * is rebuilt from the horizon snapshot, so it is empty until that read lands,
   * wrong for a few seconds after a re-seed moves every id, and hostage to
   * whatever the `patient` query returns. Pete hit the hole three times —
   * 2026-09-20 *"it did magically appear at one point"*, then 2026-09-21 *"the
   * stuff on the patient page still isn't showing up reliably"* — on a patient
   * the fixtures have known all along.
   *
   * The fixtures are local and complete, so matching the product's own patient
   * against them by name needs no network at all. The binding still wins when it
   * has an answer, because it is the id the tenant actually seeded.
   */
  const patientRef = useDemoSelector((s) => patientRefForPage(s, patientId, patientName));
  const hasEpisode = useDemoSelector((s) => !!patientRef && Object.values(s.episodes).some((e) => e.patientRef === patientRef));
  /**
   * The tenant is bound once the horizon read has come back and matched the
   * fixtures to the seeded ids. Until then **no** patient resolves, and this
   * section used to render bare `null` for as long as that took — several
   * seconds on a cold load, during which the page is the product with no demo
   * layer on it and nothing says otherwise. Pete, 2026-09-20, after losing a
   * while to exactly that: *"I don't see the stuff when I go there. Though it
   * did magically appear at one point."*
   *
   * So the three cases are told apart rather than collapsed into one `null`:
   * still waiting on either read gets the section's own shape as a skeleton, and
   * only a patient the bound tenant genuinely does not know renders nothing.
   */
  const tenantBound = useDemoSelector((s) => Object.keys(s.meta.bindings.patientIdsByRef).length > 0);
  // **No card at all when nothing needs attention** (Pete, 2026-09-21). A section
  // headed Alerts saying there are none is a thing to read and dismiss on every
  // patient who is fine, which is most of them.
  const hasAlerts = usePatientAlertRows(patientRef ?? "").length > 0;
  const named = !!patientName?.firstName && !!patientName?.lastName;
  if (!enabled) return null;
  if (!patientRef) {
    // With a name in hand there is nothing to wait for: the fixtures are already
    // in memory, so a name they do not carry is a patient this demo has no
    // business decorating. Without one we are still on the old path, where an
    // unresolved id may only mean the horizon read has not come back yet.
    const stillLoading = !named && (patientId === undefined || !tenantBound);
    return stillLoading ? <LoadingSection /> : null;
  }
  return (
    <>
      {hasAlerts && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <PatientAlertsPanel patientRef={patientRef} />
          </CardContent>
        </Card>
      )}
      {hasEpisode && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <PlanProgressSection patientRef={patientRef} />
            {/* The episode plan's detail belongs to the plan of care, not to the
                calendar (Pete, 2026-09-21). The calendar is now a generic view of
                this patient's visits behind a button beside Visit timeline and
                Upcoming Visits; the orders, the tracks, the payer gate and the
                plan versions are what the bars above are a summary of. */}
            <EpisodePlanDetail patientRef={patientRef} />
          </CardContent>
        </Card>
      )}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <PatientAvailabilityReport patientRef={patientRef} />
        </CardContent>
      </Card>
    </>
  );
}

/**
 * The section's own shape while the horizon read is still out: two cards, since
 * the alerts panel and the availability report are the two that do not wait on
 * an episode. It reserves the space rather than letting the page reflow when the
 * bind lands, and it is the difference between "still loading" and "not here".
 */
function LoadingSection() {
  return (
    <>
      {[0, 1].map((i) => (
        <Card key={i} sx={{ mb: 3 }} data-testid="home-health-patient-section-loading">
          <CardContent>
            <Stack spacing={1}>
              <Skeleton variant="text" width={180} height={28} />
              <Skeleton variant="rectangular" height={56} />
            </Stack>
          </CardContent>
        </Card>
      ))}
    </>
  );
}

/**
 * The episode plan's track table, week view, payer gate and plan versions,
 * collapsed under the plan-of-care bars and closed by default: the bars are the
 * read, and the detail is there for whoever asks what they are made of.
 */
function EpisodePlanDetail({ patientRef }: { patientRef: string }) {
  return (
    <Accordion disableGutters elevation={0} square sx={{ mt: 2, border: "1px solid #e6e6e6", "&:before": { display: "none" } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="episode-plan-detail" id="episode-plan-detail-header">
        Episode plan detail
      </AccordionSummary>
      <AccordionDetails>
        <EpisodePlanSection patientRef={patientRef} />
      </AccordionDetails>
    </Accordion>
  );
}
